import type { TimelineFishGroup } from '@/domain/fishGroups';

function lastCageId(group: TimelineFishGroup): number | null {
    return group.stays[group.stays.length - 1]?.cageId ?? null;
}

function firstCageId(group: TimelineFishGroup): number | null {
    return group.stays[0]?.cageId ?? null;
}

/**
 * Groups that continue each other in the same pen, as chains of ids, oldest first. A closed
 * group A links to B when B is the only one of A's destinations that starts in the pen A
 * ended in, and A is the only one of B's sources that ended in it. So a split, redistribution
 * or merge into an occupied pen carries the pen's row on, while two same-pen destinations (or
 * two same-pen sources) leave no single continuation and start rows of their own. Every
 * group is in exactly one chain; an unlinked group is a chain of one.
 *
 * `edges` are source → destination pairs with both ends in scope.
 */
export function cageChains(groups: TimelineFishGroup[], edges: [number, number][]): number[][] {
    const byId = new Map(groups.map((group) => [group.id, group]));
    const childrenOf = new Map<number, Set<number>>();
    const parentsOf = new Map<number, Set<number>>();
    for (const [source, destination] of edges) {
        childrenOf.set(source, (childrenOf.get(source) ?? new Set()).add(destination));
        parentsOf.set(destination, (parentsOf.get(destination) ?? new Set()).add(source));
    }

    const next = new Map<number, number>();
    const linked = new Set<number>();
    for (const group of groups) {
        const cageId = lastCageId(group);
        if (group.isLive || cageId === null) continue;
        const samePen = [...(childrenOf.get(group.id) ?? [])].filter((id) => {
            const child = byId.get(id);
            return id !== group.id && child !== undefined && firstCageId(child) === cageId;
        });
        if (samePen.length !== 1) continue;
        const [childId] = samePen;
        const samePenSources = [...(parentsOf.get(childId) ?? [])].filter((id) => {
            const source = byId.get(id);
            return source !== undefined && lastCageId(source) === cageId;
        });
        if (samePenSources.length !== 1) continue;
        next.set(group.id, childId);
        linked.add(childId);
    }

    const chains: number[][] = [];
    const placed = new Set<number>();
    for (const group of groups) {
        if (linked.has(group.id)) continue;
        const chain: number[] = [];
        for (let id: number | undefined = group.id; id !== undefined && !placed.has(id); ) {
            chain.push(id);
            placed.add(id);
            id = next.get(id);
        }
        chains.push(chain);
    }
    // Links that loop (bad data) have no head; each of their groups stands alone.
    for (const group of groups) {
        if (!placed.has(group.id)) chains.push([group.id]);
    }
    return chains;
}
